/**
 * Per-subject UI state (§12.4).
 *
 * `view:profile:<id>` / `view:group:<id>` is loaded **before the first data fetch** — every view
 * query stays disabled until `ready` flips — defaulted on `404`, and written back debounced
 * (500 ms) on every change. Persistence is server-side, so filters survive reloads and browsers.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { UI_STATE_MAX_BYTES, viewUiStateKey, type SubjectType } from '@lw/shared';
import { getUiState, putUiState } from '../../api/uiState';
import { QK } from '../../api/queryKeys';
import { useDebouncedCallback, useLatest } from '../../lib/hooks';
import { DEFAULT_VIEW_STATE, sanitizeViewState, toStoredViewState, type ViewState } from './viewState';

const WRITE_DEBOUNCE_MS = 500;

export type ViewUiStateHandle = {
  /** `false` until the stored value (or its 404) has arrived — gate every view query on this. */
  ready: boolean;
  state: ViewState;
  update: (patch: Partial<ViewState>) => void;
  /** Loading the state failed; the screen offers a retry rather than guessing. */
  error: unknown;
  retry: () => void;
};

export function useViewUiState(subjectType: SubjectType, id: number): ViewUiStateHandle {
  const key = viewUiStateKey(subjectType, id);

  const query = useQuery({
    queryKey: QK.uiState(key),
    queryFn: ({ signal }) => getUiState<unknown>(key, signal),
    staleTime: Number.POSITIVE_INFINITY,
    // A cached value from a previous visit is authoritative; refetching would race the writer.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const [state, setState] = useState<ViewState | null>(null);
  /** Subject whose *stored* value has been applied. */
  const seededKey = useRef<string | null>(null);
  /** Subject that fell back to defaults because the read failed. */
  const fallbackKey = useRef<string | null>(null);

  // Seed once per subject: from whatever the server had, from the defaults on 404, and — so a
  // broken ui-state endpoint can never brick the screen — from the defaults on failure too. A
  // successful retry still replaces the fallback with the stored value.
  useEffect(() => {
    if (query.isSuccess && seededKey.current !== key) {
      seededKey.current = key;
      fallbackKey.current = null;
      setState(sanitizeViewState(query.data));
      return;
    }
    if (query.isError && seededKey.current !== key && fallbackKey.current !== key) {
      fallbackKey.current = key;
      setState(sanitizeViewState(null));
    }
  }, [query.isSuccess, query.isError, query.data, key]);

  // Switching subjects re-arms the seeding above.
  useEffect(() => {
    if (seededKey.current !== null && seededKey.current !== key) setState(null);
  }, [key]);

  const persist = useDebouncedCallback((next: ViewState) => {
    const payload = toStoredViewState(next);
    // The endpoint caps the body at 16 KB (§5.1 #32); this state is orders of magnitude smaller,
    // but a pathological `q` is still worth refusing locally rather than 413-ing.
    if (JSON.stringify(payload).length > UI_STATE_MAX_BYTES) return;
    void putUiState(key, payload).catch(() => {
      // Persistence is best-effort: the screen keeps working with in-memory state.
    });
  }, WRITE_DEBOUNCE_MS);

  // Persistence is driven from a ref rather than from inside the state updater: React skips
  // updaters for an unmounted component, and the last change before navigating away must still
  // reach the server when the debounce flushes.
  const stateRef = useLatest(state);

  const update = useCallback(
    (patch: Partial<ViewState>) => {
      const next: ViewState = { ...(stateRef.current ?? DEFAULT_VIEW_STATE), ...patch };
      setState(next);
      persist(next);
    },
    [persist, stateRef],
  );

  return {
    ready: state !== null,
    state: state ?? DEFAULT_VIEW_STATE,
    update,
    error: query.isError ? query.error : null,
    retry: () => void query.refetch(),
  };
}
